import React, {useState, useEffect, useCallback} from "react";
import {Table, Input, Typography, Row, Col, Button, Space, Modal, Result, Select} from "antd";
import {useMediaQuery} from "react-responsive";
import {motion} from "framer-motion";
import {SearchOutlined, ArrowLeftOutlined, UndoOutlined} from "@ant-design/icons";
import {useParams, useNavigate} from "react-router-dom";
import theme from "../theme";
import peopleService from "../services/PeopleService";
import formatDate from "../utils/dateUtils";
import PersonEditForm from "../components/people/PersonEditForm";
import {COT_ROLE, DISTRICT_ROLE, SECTIONAL_ROLE, useUserRole} from "../utils/userSessionUtils";
import sectionalService from "../services/SectionalsService";
import promoterService from "../services/PromoterService";

const {Title, Text} = Typography;
const {Option} = Select;

const PeoplePageAsUser = () => {
    const {username: electorKey} = useParams();
    const {from: fromPage} = useParams();
    const isMobile = useMediaQuery({maxWidth: 800});
    const navigate = useNavigate();

    const [people, setPeople] = useState([]);
    const [promoter, setPromoter] = useState(null);
    const [loading, setLoading] = useState(false);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const [pagination, setPagination] = useState({current: 1, pageSize: 10, total: 0});
    const [selectedPerson, setSelectedPerson] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const userRole = useUserRole();

    const [filters, setFilters] = useState({
        createdBy: {value: electorKey, operator: "eq"},
        name: {value: "", operator: "like"},
        firstLastName: {value: "", operator: "like"},
        secondLastName: {value: "", operator: "like"},
        phone: {value: "", operator: "like"},
        sectionId: {value: [], operator: "in"},
    });

    const [tempFilters, setTempFilters] = useState({
        name: "",
        firstLastName: "",
        secondLastName: "",
        phone: "",
        sectionId: [],
    });

    const fetchPromoter = useCallback(async () => {
        setLoading(true);
        try {
            let response;
            if (fromPage === 'PROMOTERS') {
                response = await promoterService.getAll({
                    electorKey: {value: electorKey, operator: "eq"}
                });
            } else {
                response = await sectionalService.getAll({
                    electorKey: {value: electorKey, operator: "eq"}
                });
            }
            const found = response.data.find((p) => p.electorKey === electorKey);
            setPromoter(found || null);
        } catch (error) {
            console.error("Error al buscar promotor:", error);
        } finally {
            setLoading(false);
            setInitialLoadComplete(true);
        }
    }, [electorKey, fromPage]);

    const fetchPeople = useCallback(async (page, pageSize, appliedFilters) => {
        setLoading(true);
        try {
            const response = await peopleService.getAll(page, pageSize, appliedFilters);
            setPeople(response.data);
            setPagination((prev) => ({
                ...prev,
                current: page,
                pageSize,
                total: response.meta.totalItems,
            }));
        } catch (err) {
            console.error("Error al cargar registros:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const initialFilters = {
            createdBy: {value: electorKey, operator: "eq"},
            name: {value: "", operator: "like"},
            firstLastName: {value: "", operator: "like"},
            secondLastName: {value: "", operator: "like"},
            phone: {value: "", operator: "like"},
            sectionId: {value: [], operator: "in"},
        };
        fetchPromoter().catch(console.error);
        fetchPeople(1, 10, initialFilters).catch(console.error);
    }, [electorKey, fetchPromoter, fetchPeople]);

    const handleFilterChange = (key, value) => {
        setTempFilters((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const applyFilters = () => {
        const newFilters = {
            createdBy: {value: electorKey, operator: "eq"},
            name: {value: tempFilters.name, operator: "like"},
            firstLastName: {value: tempFilters.firstLastName, operator: "like"},
            secondLastName: {value: tempFilters.secondLastName, operator: "like"},
            phone: {value: tempFilters.phone, operator: "like"},
            sectionId: {value: tempFilters.sectionId, operator: "in"},
        };
        setFilters(newFilters);
        fetchPeople(1, pagination.pageSize, newFilters).catch(console.error);
    };

    const resetFilters = () => {
        const resetTempFilters = {
            name: "",
            firstLastName: "",
            secondLastName: "",
            phone: "",
            sectionId: [],
        };
        const resetAppliedFilters = {
            createdBy: {value: electorKey, operator: "eq"},
            name: {value: "", operator: "like"},
            firstLastName: {value: "", operator: "like"},
            secondLastName: {value: "", operator: "like"},
            phone: {value: "", operator: "like"},
            sectionId: {value: [], operator: "in"},
        };
        setTempFilters(resetTempFilters);
        setFilters(resetAppliedFilters);
        fetchPeople(1, pagination.pageSize, resetAppliedFilters).catch(console.error);
    };

    const handleViewClick = (record) => {
        setSelectedPerson(record);
        setIsModalVisible(true);
    };

    const handleCloseModal = () => {
        setIsModalVisible(false);
        setSelectedPerson(null);
        fetchPeople(pagination.current, pagination.pageSize, filters).catch(console.error);
    };

    const handleTableChange = (newPagination) => {
        fetchPeople(newPagination.current, newPagination.pageSize, filters).catch(console.error);
    };

    const columns = [
        {title: "Clave de Elector", dataIndex: "electorKey", key: "electorKey"},
        {
            title: "Nombre",
            key: "fullName",
            render: (record) => `${record.name} ${record.firstLastName} ${record.secondLastName || ""}`,
        },
        {title: "Teléfono", dataIndex: "phone", key: "phone"},
        {
            title: "Dirección",
            key: "address",
            render: (record) => `${record.street}, ${record.neighborhood}, CP ${record.zipCode}`,
        },
        {title: "Sección", dataIndex: "sectionId", key: "sectionId"},
        {
            title: "Fecha de creación",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (createdAt) => formatDate(createdAt),
        },
        {
            title: "Fecha de actualización",
            dataIndex: "updatedAt",
            key: "updatedAt",
            render: (updatedAt) => formatDate(updatedAt),
        },
    ];

    const mobileColumns = [
        {
            title: "Nombre",
            key: "fullName",
            render: (record) => `${record.name} ${record.firstLastName}`,
        },
        {title: "Teléfono", dataIndex: "phone", key: "phone"},
    ];

    if (!initialLoadComplete) {
        return null;
    }

    if (![DISTRICT_ROLE, COT_ROLE, SECTIONAL_ROLE].includes(userRole)) {
        return (
            <Result
                status="403"
                title="403"
                subTitle="No tienes permiso para ver esta página."
                extra={
                    <Button type="primary" icon={<ArrowLeftOutlined/>} onClick={() => navigate('/promotores')}>
                        Regresar
                    </Button>
                }
            />
        );
    }


    if (!promoter) {
        return (
            <Result
                status="403"
                title="403"
                subTitle="No tienes permiso para ver los registros de este convencido."
                extra={
                    <Button type="primary" icon={<ArrowLeftOutlined/>} onClick={() => navigate('/promotores')}>
                        Regresar
                    </Button>
                }
            />
        );
    }

    return (
        <>
            <motion.div
                initial={{opacity: 0, y: -10}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.5}}
                style={{
                    maxWidth: isMobile ? "100%" : "90%",
                    width: "90vw",
                    margin: "20px auto",
                    background: theme.colors.card,
                    padding: "20px",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    overflowX: "auto",
                }}
            >
                <Button
                    type="primary"
                    danger
                    icon={<ArrowLeftOutlined/>}
                    onClick={() => navigate(fromPage === "PROMOTERS" ? '/promotores' : '/seccionales')}
                >
                    Regresar al listado de { fromPage === "PROMOTERS" ? 'promotores' : 'seccionales' }
                </Button>

                <Title level={2} style={{textAlign: "center"}}>
                    Registros de <p style={{color: theme.colors.primary}}>{promoter.fullName || (promoter.name + " " + promoter.firstLastName + " " + promoter.secondLastName)}</p>
                </Title>

                <Row gutter={[16, 16]} style={{marginBottom: "20px"}}>
                    <Col xs={24} sm={12} md={8}>
                        <Input
                            placeholder="Nombre"
                            value={tempFilters.name}
                            onChange={(e) => handleFilterChange("name", e.target.value)}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Input
                            placeholder="Apellido Paterno"
                            value={tempFilters.firstLastName}
                            onChange={(e) => handleFilterChange("firstLastName", e.target.value)}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Input
                            placeholder="Apellido Materno"
                            value={tempFilters.secondLastName}
                            onChange={(e) => handleFilterChange("secondLastName", e.target.value)}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Input
                            placeholder="Teléfono"
                            value={tempFilters.phone}
                            onChange={(e) => handleFilterChange("phone", e.target.value)}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Select
                            mode="multiple"
                            placeholder="Sección"
                            value={tempFilters.sectionId}
                            onChange={(value) => handleFilterChange("sectionId", value)}
                            style={{width: "100%"}}
                        >
                            {promoter.sections && promoter.sections.map((sec) => (
                                <Option key={sec} value={sec}>{`Sección ${sec}`}</Option>
                            ))}
                            {promoter.sectionId && (
                                <Option key={promoter.sectionId}
                                        value={promoter.sectionId}>{`Sección ${promoter.sectionId}`}</Option>
                            )}
                        </Select>
                    </Col>
                </Row>

                <div style={{display: "flex", justifyContent: "flex-end", marginBottom: "20px"}}>
                    <Space>
                        <Button type="primary" icon={<SearchOutlined/>} onClick={applyFilters}>
                            Aplicar Filtros
                        </Button>
                        <Button icon={<UndoOutlined/>} onClick={resetFilters}>Restablecer</Button>
                    </Space>
                </div>

                <Table
                    columns={isMobile ? mobileColumns : columns}
                    dataSource={people}
                    rowKey="id"
                    pagination={{...pagination, showSizeChanger: true, pageSizeOptions: ["1", "5", "10", "20"]}}
                    loading={loading}
                    onChange={handleTableChange}
                    scroll={{x: "max-content"}}
                    onRow={(record) => ({
                        onClick: () => handleViewClick(record),
                        style: {cursor: "pointer"},
                    })}
                />

                <Text style={{textAlign: "right", display: "block", marginTop: "10px"}}>
                    Mostrando {people.length} de {pagination.total} registros
                </Text>
            </motion.div>

            {selectedPerson && (
                <Modal
                    open={isModalVisible}
                    onCancel={handleCloseModal}
                    footer={null}
                    width={isMobile ? "90%" : "45%"}
                >
                    <PersonEditForm person={selectedPerson} mode="view" onClose={handleCloseModal}/>
                </Modal>
            )}
        </>
    );
};

export default PeoplePageAsUser;